import React, {
    createContext,
    PropsWithChildren,
    useContext,
    useEffect,
    useReducer,
    useRef,
    useState,
} from 'react';
import i18n from 'i18next';
import { getDefaultLocale } from '@low/i18n';
import { readLocallyStored, writeLocallyStored } from '@ringcentral/rwc-utils/src/local-storage';
import { getPrefixedLogger, prefixes } from '@ringcentral/rwc-utils/src/rwc-logger';

import { getBrandId, isDynamicBrand } from '@ringcentral/rwc-branding/src/utils';
import { SdkContext } from '@low/containers/sdk-context';
import {
    BRAND_CONFIG_REDUCER_TYPE,
    brandConfigReducer,
} from '@low/containers/brand-config/brand-config-reducer';
import { BrandConfig, BrandConfigSourceType } from '@low/dynamic/types';

import { BssManager } from '@low/dynamic/bss-manager';
import { LocalBrandConfigManager } from '@low/dynamic/local-brand-config-manager';
import { featureFlagClient } from '@ringcentral/rwc-utils/src/feature-flag-client';
import {
    DynamicBrandingErrorReport,
    handleErrorReport,
} from '@ringcentral/rwc-utils/src/error-report';
import { FEATURE_FLAGS } from '@ringcentral/rwc-utils/src/constants/feature-flags';
import rwcInternalApi, { intApiBrandConfig } from '@ringcentral/rwc-utils/src/internal-api';
import { AuthStatus } from '@low/types';

const isBssEnabled = featureFlagClient.getFlag(FEATURE_FLAGS.enableBss).enabled;
const locale = readLocallyStored('locale', getDefaultLocale()) ?? 'en-US';

const bssManager = new BssManager({ brandId: getBrandId(), lang: locale });
const localBrandConfigManager = new LocalBrandConfigManager({
    brandId: getBrandId(),
    lang: locale,
});
const logger = getPrefixedLogger(prefixes.VC_CONTAINERS, `dynamic-branding[${getBrandId()}]`);
const bssConfigCacheKey = 'bssConfigCache';
const cachedConfig = readLocallyStored(bssConfigCacheKey, null, true);

const initBrandConfig: BrandConfig = {
    source: BrandConfigSourceType.INIT,
    brandId: '',
    displayName: '',
    productName: '',
    shortDisplayName: '',
    notificationAppPrefixName: '',
    legalInfoPage: '',
    termsOfServicePage: '',
    privacyPolicyPage: '',
    trustCenterPage: '',
    supportRootPage: '',
    launchYear: '',
    copyright: '',
    notificationTradeMark: '',
    privacyControlsHelpPage: '',
    downloadLink: {
        android: '',
        ios: '',
    },
    knowledgeBase: {
        systemRequirementsLink: '',
        chromaKeyLink: '',
        hardwareAccelerationLink: '',
        thirdPartyCameraLink: '',
    },
    assets: {
        mainLogoUrl: '',
        faviconUrl: '',
        blackLogoUrl: '',
        topPanelLogoUrl: '',
        watermarkUrl: '',
        poweredByLogoUrl: '',
        videoAboutIcon: '',
        topPanelColor: '',
        settingColor: '',
        settingActiveColor: '',
        settingActiveBgColor: '',
    },
};

export const BrandConfigContext = createContext<{ brandConfig: BrandConfig }>({
    brandConfig: initBrandConfig,
});

const ErrorMessage = () => (
    <div className='config-load-error'>
        <p className='config-load-error-message'>{i18n.t('banner.APPLICATION_UNAVAILABLE')}</p>
    </div>
);

const BrandConfigProvider = ({
    children,
    localeInitialized,
    afterConfigLoaded = () => null,
}: PropsWithChildren<{
    afterConfigLoaded: (config: BrandConfig) => void;
    localeInitialized: boolean;
}>) => {
    const [showConfigLoadError, setShowConfigLoadError] = useState(false);
    const afterConfigLoadedRef = useRef(afterConfigLoaded);
    afterConfigLoadedRef.current = afterConfigLoaded;

    const [state, dispatch] = useReducer(brandConfigReducer, initBrandConfig);

    const sdkContext = useContext(SdkContext);
    const sdkInstance = sdkContext?.sdk();
    const authStatus = sdkContext?.authStatus;

    /**
     * if current brand is not dynamic or bss is disabled, then fetch local config
     */
    const getLocalConfigWhenBssDisabled = async () => {
        try {
            await localBrandConfigManager.fetchLocalConfig();
            dispatch({
                type: BRAND_CONFIG_REDUCER_TYPE.UPDATE_FROM_LOCAL,
                payload: localBrandConfigManager.getConfig(),
            });
        } catch (e) {
            setShowConfigLoadError(true);
            logger.error('[LOCAL] get local brand config failed, show error tip');

            const report = new DynamicBrandingErrorReport(e as Error, {
                brandId: getBrandId(),
                type: 'GetBSSFailed',
            });

            handleErrorReport(report);
        }
    };

    /**
     * fetch bss base config and locale config
     */
    const updateFromBss = async discoveryData => {
        if (!discoveryData || !discoveryData.assetService) {
            logger.info('get asset service url failed');
            throw new Error('get asset service url failed');
        }

        const bssUrlRef = `${discoveryData.assetService?.bssUri}/${discoveryData.assetService?.assetId}`;
        await bssManager.fetchBssConfig(bssUrlRef);
        dispatch({
            type: BRAND_CONFIG_REDUCER_TYPE.UPDATE_FROM_BSS,
            payload: bssManager.getConfig(),
        });
    };

    /**
     * get discovery data,since bss url and assetId is from initial and external api
     * in Hercules mode, we don't use rc sdk, so before we needed to fetch discovery data manually
     * but after Jupiter Web 23.4.20 we will get bssUri and assetId from Jupiter
     * in Rcv web, discovery api is called by js sdk
     * @returns
     */
    const getDiscoveryData = async () => {
        let externalData = await sdkInstance?.platform().discovery().externalData();
        if (externalData) {
            return externalData;
        }

        await sdkInstance?.platform().discovery().refreshExternalData();
        externalData = await sdkInstance?.platform().discovery().externalData();
        return externalData
            ? externalData
            : await sdkInstance?.platform().discovery().fetchInitialData();
    };

    /**
     * init config context logic
     */
    const init = async () => {
        logger.info(`isDynamicBrand:${isDynamicBrand},isBssEnabled:${isBssEnabled}`);
        if (isDynamicBrand && isBssEnabled) {
            // use the local config to render component, then fetch bss after get accessToken
            if (cachedConfig) {
                dispatch({
                    type: BRAND_CONFIG_REDUCER_TYPE.UPDATE_FROM_CACHE,
                    payload: cachedConfig,
                });
            }

            try {
                const discoveryData = await getDiscoveryData();
                await updateFromBss(discoveryData);
            } catch (e) {
                if (!cachedConfig) {
                    logger.error(
                        '[BSS] get bss failed, and has no cached config, show error tip',
                        e
                    );
                    setShowConfigLoadError(true);
                } else {
                    logger.info('[BSS] get bss failed, use cached config');
                }

                const report = new DynamicBrandingErrorReport(e as Error, {
                    brandId: getBrandId(),
                    locale,
                    type: 'GetBSSFailed',
                });

                handleErrorReport(report);
            }
        } else {
            getLocalConfigWhenBssDisabled();
        }
    };

    useEffect(() => {
        if (authStatus === AuthStatus.SUCCESS) {
            init();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authStatus]);

    useEffect(() => {
        /**
         * get bss config successfully
         */
        if (
            [BrandConfigSourceType.BSS, BrandConfigSourceType.BSS_FALLBACK].includes(state.source)
        ) {
            writeLocallyStored(bssConfigCacheKey, state, true);
        }

        /**
         * get Cache or bss config successfully.
         */
        if (state.source !== BrandConfigSourceType.INIT) {
            afterConfigLoadedRef.current(state);
            logger.info(`get brand config successfully, data source [${state.source}]`);
        }

        rwcInternalApi.register(intApiBrandConfig, state);
        return () => {
            rwcInternalApi.unregister(intApiBrandConfig);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.source]);

    if (!localeInitialized) {
        return null;
    }

    return showConfigLoadError ? (
        <ErrorMessage />
    ) : (
        <BrandConfigContext.Provider value={{ brandConfig: state }}>
            {children}
        </BrandConfigContext.Provider>
    );
};

export default BrandConfigProvider;
